import {
    FormatBoldRounded,
    FormatItalicRounded,
    FormatUnderlinedRounded,
    StrikethroughSRounded,
    FormatClearRounded,
    LinkRounded,
    FormatAlignLeftRounded,
    FormatAlignCenterRounded,
    FormatAlignRightRounded,
    FormatColorTextRounded,
} from "@mui/icons-material";
import { Divider, IconButton, Stack, Tooltip, Paper, Box, InputBase } from "@mui/material";
import { useDispatch } from "react-redux";
import { useRef, useState, useEffect } from "react";
import { reduxHelper } from "../../../config/reduxHelper";
import { CheckRounded, CloseRounded, FormatSizeRounded } from "@mui/icons-material";

const Sep = () => (
    <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.25 }} />
);

export default function FloatingTextToolbar({ selectionRect, absolute = true }) {
    const dispatch = useDispatch();
    const colorInputRef = useRef(null);
    const savedRangeRef = useRef(null);
    const tempLinkHighlightRef = useRef(null);
    const [linkMode, setLinkMode] = useState(false);
    const [url, setUrl] = useState("");
    const [showFontSize, setShowFontSize] = useState(false);
    const [activeStyles, setActiveStyles] = useState({
        bold: false,
        italic: false,
        underline: false,
        strikeThrough: false,
        justifyLeft: false,
        justifyCenter: false,
        justifyRight: false,
        link: false,
    });

    const refreshActiveStyles = () => {
        const safeState = (cmd) => {
            try {
                return document.queryCommandState(cmd);
            } catch (e) {
                return false;
            }
        };

        const selection = window.getSelection();
        let isLink = false;
        if (selection && selection.rangeCount > 0) {
            let container = selection.getRangeAt(0).commonAncestorContainer;
            if (container.nodeType === 3) container = container.parentNode;
            isLink = !!container.closest("a");
        }

        setActiveStyles({
            bold: safeState("bold"),
            italic: safeState("italic"),
            underline: safeState("underline"),
            strikeThrough: safeState("strikeThrough"),
            justifyLeft: safeState("justifyLeft"),
            justifyCenter: safeState("justifyCenter"),
            justifyRight: safeState("justifyRight"),
            link: isLink,
        });
    };

    useEffect(() => {
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
            savedRangeRef.current = selection.getRangeAt(0).cloneRange();
        }
        refreshActiveStyles();
    }, [selectionRect]);

    useEffect(() => {
        const onSelectionChange = () => {
            if (!selectionRect) return;
            refreshActiveStyles();
        };
        document.addEventListener("selectionchange", onSelectionChange);
        return () => document.removeEventListener("selectionchange", onSelectionChange);
    }, [selectionRect]);

    const withSavedSelection = (callback) => {
        const selection = window.getSelection();
        if (savedRangeRef.current && selection) {
            selection.removeAllRanges();
            selection.addRange(savedRangeRef.current);
        }
        callback();
        if (selection && selection.rangeCount > 0) {
            savedRangeRef.current = selection.getRangeAt(0).cloneRange();
        }
    };

    const clearTempLinkHighlight = () => {
        const marker = tempLinkHighlightRef.current;
        if (!marker || !marker.parentNode) return;

        const parent = marker.parentNode;
        while (marker.firstChild) {
            parent.insertBefore(marker.firstChild, marker);
        }
        parent.removeChild(marker);
        parent.normalize();
        tempLinkHighlightRef.current = null;
    };

    const applyTempLinkHighlight = () => {
        if (!savedRangeRef.current || savedRangeRef.current.collapsed) return;
        clearTempLinkHighlight();

        const marker = document.createElement("span");
        marker.setAttribute("data-sb-temp-link-highlight", "true");
        marker.style.backgroundColor = "rgba(25, 118, 210, 0.18)";
        marker.style.borderRadius = "2px";

        const extracted = savedRangeRef.current.extractContents();
        marker.appendChild(extracted);
        savedRangeRef.current.insertNode(marker);
        tempLinkHighlightRef.current = marker;

        // Keep the highlighted text visibly selected right after clicking Link.
        const selection = window.getSelection();
        if (selection) {
            const range = document.createRange();
            range.selectNodeContents(marker);
            selection.removeAllRanges();
            selection.addRange(range);
            savedRangeRef.current = range.cloneRange();
        }
    };

    useEffect(() => {
        if (!linkMode) {
            clearTempLinkHighlight();
        }
    }, [linkMode]);

    useEffect(() => () => {
        clearTempLinkHighlight();
    }, []);

    const handleFormat = (cmd, value = null) => {
        withSavedSelection(() => {
            document.execCommand(cmd, false, value);
        });
        setTimeout(refreshActiveStyles, 0);
    };

    const handleApplyLink = () => {
        const trimmedUrl = (url || "").trim();
        if (trimmedUrl) {
            const selection = window.getSelection();
            if (selection && tempLinkHighlightRef.current) {
                const range = document.createRange();
                range.selectNodeContents(tempLinkHighlightRef.current);
                selection.removeAllRanges();
                selection.addRange(range);
                document.execCommand("createLink", false, trimmedUrl);
            } else {
                handleFormat("createLink", trimmedUrl);
            }
        }
        clearTempLinkHighlight();
        setLinkMode(false);
        setTimeout(refreshActiveStyles, 0);
    };

    const handleSetLinkMode = () => {
        const selection = window.getSelection();
        if (selection.rangeCount > 0) {
            savedRangeRef.current = selection.getRangeAt(0).cloneRange();
            let container = selection.getRangeAt(0).commonAncestorContainer;
            if (container.nodeType === 3) container = container.parentNode;

            const link = container.closest("a");
            if (link) {
                setUrl(link.href);
            } else {
                setUrl("");
            }
            applyTempLinkHighlight();
        }
        setLinkMode(true);
    };

    const ib = (title, Icon, onClick, active = false) => (
        <Tooltip title={title} key={title} placement="top">
            <IconButton
                size="small"
                onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onClick();
                }}
                sx={{
                    color: active ? "primary.main" : "text.primary",
                    bgcolor: active ? "primary.lighter" : "transparent",
                    borderRadius: 1,
                    p: 0.4,
                    "&:hover": { bgcolor: active ? "primary.lighter" : "action.hover" },
                }}
            >
                <Icon sx={{ fontSize: 18 }} />
            </IconButton>
        </Tooltip>
    );

    if (!selectionRect) return null;

    return (
        <Paper
            elevation={8}
            onMouseDown={(e) => e.preventDefault()}
            onMouseUp={(e) => e.stopPropagation()}
            sx={{
                position: absolute ? 'fixed' : 'absolute',
                top: (absolute ? selectionRect.top : (selectionRect.top || 0)) - 60,
                left: (absolute ? selectionRect.left : (selectionRect.left || 0)) + (selectionRect.width / 2),
                transform: 'translateX(-50%)',
                zIndex: 9999,
                display: 'flex',
                alignItems: 'center',
                gap: 0.25,
                p: 0.5,
                borderRadius: '50px',
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                boxShadow: '0px 10px 40px rgba(0,0,0,0.12)',
            }}
        >
            {linkMode ? (
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ px: 1 }}>
                    <InputBase
                        placeholder="Enter URL..."
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleApplyLink()}
                        sx={{ fontSize: '0.75rem', width: 150 }}
                        onMouseDown={(e) => e.stopPropagation()}
                    />
                    <IconButton
                        size="small"
                        onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                        onClick={handleApplyLink}
                    >
                        <CheckRounded fontSize="inherit" />
                    </IconButton>
                    <IconButton
                        size="small"
                        onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                        onClick={() => {
                            clearTempLinkHighlight();
                            setLinkMode(false);
                            setTimeout(refreshActiveStyles, 0);
                        }}
                    >
                        <CloseRounded fontSize="inherit" />
                    </IconButton>
                </Stack>
            ) : showFontSize ? (
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ px: 1 }}>
                    {[1, 2, 3, 4, 5, 6, 7].map((size) => (
                        <IconButton
                            key={size}
                            size="small"
                            onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleFormat("fontSize", size);
                                setShowFontSize(false);
                            }}
                            sx={{ fontSize: '0.75rem', p: 0.5 }}
                        >
                            {size}
                        </IconButton>
                    ))}
                    <Sep />
                    <IconButton
                        size="small"
                        onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setShowFontSize(false);
                        }}
                    >
                        <CloseRounded fontSize="inherit" />
                    </IconButton>
                </Stack>
            ) : (
                <>
                    {/* Color Picker */}
                    <input
                        type="color"
                        ref={colorInputRef}
                        style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', width: 0, height: 0 }}
                        onChange={(e) => handleFormat("foreColor", e.target.value)}
                    />
                    {ib("Text Color", FormatColorTextRounded, () => colorInputRef.current?.click())}

                    <Sep />

                    {/* Font Size */}
                    {ib("Font Size", FormatSizeRounded, () => setShowFontSize(true))}

                    <Sep />

                    {/* Basic Formatting Group */}
                    <Stack direction="row" spacing={0.25}>
                        {ib("Bold", FormatBoldRounded, () => handleFormat("bold"), activeStyles.bold)}
                        {ib("Italic", FormatItalicRounded, () => handleFormat("italic"), activeStyles.italic)}
                        {ib("Underline", FormatUnderlinedRounded, () => handleFormat("underline"), activeStyles.underline)}
                        {ib("Strikethrough", StrikethroughSRounded, () => handleFormat("strikeThrough"), activeStyles.strikeThrough)}
                        {ib("Clear Style", FormatClearRounded, () => handleFormat("removeFormat"))}
                    </Stack>

                    <Sep />

                    {/* Alignment Group */}
                    <Stack direction="row" spacing={0.25}>
                        {ib("Align Left", FormatAlignLeftRounded, () => handleFormat("justifyLeft"), activeStyles.justifyLeft)}
                        {ib("Align Center", FormatAlignCenterRounded, () => handleFormat("justifyCenter"), activeStyles.justifyCenter)}
                        {ib("Align Right", FormatAlignRightRounded, () => handleFormat("justifyRight"), activeStyles.justifyRight)}
                    </Stack>

                    <Sep />

                    {/* Link Group */}
                    {ib("Link", LinkRounded, handleSetLinkMode, activeStyles.link || linkMode)}
                </>
            )}
        </Paper>
    );
}
